/**
 * PlanBuilderNavigator
 *
 * Orchestrates the plan builder flow.
 * Uses a native stack navigator with the PlanBuilderContext
 * for shared state. Screens are registered statically but
 * navigation is driven by the dynamic stack builder.
 */

import React, { useCallback, useEffect, useRef } from "react";
import { Alert } from "react-native";
import { createNativeStackNavigator } from "@react-navigation/native-stack";
import { CommonActions, useNavigation } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { useToast } from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import { useFlowAnalytics } from "@src/packages/analytics/useFlowAnalytics";
import { usePlanBuilder } from "./PlanBuilderContext";
import {
  getNextScreen,
  getFirstIncompleteScreen,
  shouldAskForAcceptedPlan,
} from "./buildPlanBuilderStack";
import type {
  PlanBuilderScreen,
  PlanBuilderState,
  KnownResults,
} from "./types";
import {
  buildPlanBuilderAddress,
  buildPlanBuilderDiseases,
} from "./planBuilderPatientPayload";
import { useQueuedPatientUpdate } from "@src/packages/patients/sync/usePatientSync";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import {
  useCreateTestResults,
  type CreateTestResultPayload,
} from "@src/packages/test-results/hooks/useTestResults";
import type { UpdatePatientPayload } from "@louise/patients/patients.types";
import {
  useAcceptTreatmentPlan,
  useTreatmentPlan,
} from "../hooks/useTreatmentPlan";

// Screens
import GoalRecapScreen from "./screens/GoalRecapScreen";
import ModalityScreen from "./screens/ModalityScreen";
import AcceptedPlanScreen from "./screens/AcceptedPlanScreen";
import PastCyclesScreen from "./screens/PastCyclesScreen";
import PastCycleDetailsScreen from "./screens/PastCycleDetailsScreen";
import ConditionsScreen from "./screens/ConditionsScreen";
import EthnicityScreen from "./screens/EthnicityScreen";
import PartnerStatusScreen from "./screens/PartnerStatusScreen";
import ClinicStatusScreen from "./screens/ClinicStatusScreen";
import KnownResultsScreen from "./screens/KnownResultsScreen";
import LocationInsuranceScreen from "./screens/LocationInsuranceScreen";
import TimelineScreen from "./screens/TimelineScreen";
import FreeTextScreen from "./screens/FreeTextScreen";
import BuildingScreen from "./screens/BuildingScreen";
import PlanRevealScreen from "./screens/PlanRevealScreen";
import PlanBuilderScreenLayout from "./components/PlanBuilderScreenLayout";

type FertilityIntent = NonNullable<UpdatePatientPayload["fertilityIntent"]>;

/** Map goal values (HomepageGoal) to API fertilityIntent.goal values */
const GOAL_TO_API: Record<string, NonNullable<FertilityIntent["goal"]>> = {
  "embryo-preserve": "embryo-preserve",
  pregnancy: "pregnancy",
  "egg-donation": "egg-donation",
  "sperm-donation": "sperm-donation",
  adoption: "adoption",
  surrogacy: "surrogacy",
};

/** Resolve preserve goal to the correct API value based on gender */
function resolveGoalForAPI(
  state: PlanBuilderState,
): NonNullable<FertilityIntent["goal"]> | undefined {
  if (!state.goal) return undefined;
  if (state.goal === "preserve") {
    return state.gender === "male" ? "sperm-freezing" : "egg-freezing";
  }
  return GOAL_TO_API[state.goal];
}

/** Convert known results from PlanBuilder into test result API payloads */
function buildTestResultPayloads(
  results: KnownResults,
): CreateTestResultPayload[] {
  const payloads: CreateTestResultPayload[] = [];
  const today = new Date().toISOString().slice(0, 10);

  if (results.hasAMH && results.amhValue != null) {
    payloads.push({
      name: "AMH (Anti-Müllerian Hormone)",
      type: "blood",
      bloodTestCategory: "hormonal",
      patientValue: String(results.amhValue),
      valueType: "numeric",
      unit: "ng/mL",
      date: today,
      notes: "Self-reported during plan builder",
    });
  }

  if (results.hasFSH && results.fshValue != null) {
    payloads.push({
      name: "FSH (Follicle-Stimulating Hormone)",
      type: "blood",
      bloodTestCategory: "hormonal",
      patientValue: String(results.fshValue),
      valueType: "numeric",
      unit: "mIU/mL",
      date: today,
      notes: "Self-reported during plan builder",
    });
  }

  return payloads;
}

/** Map clinic status to API fertilityIntent.stage */
const CLINIC_STATUS_TO_STAGE: Record<string, string> = {
  "no-clinic": "exploring-options",
  researching: "exploring-options",
  "at-clinic": "getting-ready",
};

/** Build the API payload from current builder state */
function buildPayload(
  state: PlanBuilderState,
  primaryAddressId?: string,
): UpdatePatientPayload {
  const diseases = buildPlanBuilderDiseases(state.conditions);
  const hasLoss = state.conditions.includes("loss");
  const hasCancer = state.conditions.includes("cancer");

  return {
    dateOfBirth: state.dateOfBirth ?? undefined,
    ethnicity:
      state.ethnicity.length > 0 ? state.ethnicity.join(", ") : undefined,
    partnerChoice: state.partnerStatus ?? undefined,
    timeline: state.timeline ?? undefined,
    addresses: buildPlanBuilderAddress(state.zipCode, primaryAddressId),
    fertilityIntent: {
      plannedModalities: state.modalities,
      ...(resolveGoalForAPI(state)
        ? {
            goal: resolveGoalForAPI(state) as NonNullable<
              NonNullable<UpdatePatientPayload["fertilityIntent"]>["goal"]
            >,
          }
        : {}),
      ...(state.hasEmployerBenefit != null
        ? {
            hasEmployerBenefits: state.hasEmployerBenefit,
            // Keep a provider only for an affirmative answer. This avoids a
            // stale program showing after someone changes their answer to No.
            employerBenefit: state.hasEmployerBenefit
              ? state.employerBenefit
              : null,
          }
        : {}),
      ...(state.clinicStatus && CLINIC_STATUS_TO_STAGE[state.clinicStatus]
        ? {
            stage: CLINIC_STATUS_TO_STAGE[state.clinicStatus] as
              | "trying-naturally"
              | "exploring-options"
              | "getting-ready"
              | "in-treatment",
          }
        : {}),
    },
    ...(diseases || hasCancer
      ? {
          medicalHistory: {
            ...(diseases ? { diseases } : {}),
            ...(hasCancer ? { cancers: [{ cancer: "unspecified" }] } : {}),
          },
        }
      : {}),
    ...(hasLoss
      ? { obGynHistory: { pregnancies: [{ outcome: "miscarriage" }] } }
      : {}),
  };
}

type PlanBuilderStackParamList = {
  GoalRecap: undefined;
  ModalityScreen: undefined;
  AcceptedPlan: undefined;
  PastCycles: undefined;
  PastCycleDetails: undefined;
  Conditions: undefined;
  Ethnicity: undefined;
  PartnerStatus: undefined;
  ClinicStatus: undefined;
  KnownResults: undefined;
  LocationInsurance: undefined;
  Timeline: undefined;
  FreeText: undefined;
  Building: undefined;
  PlanReveal: undefined;
};

const Stack = createNativeStackNavigator<PlanBuilderStackParamList>();

/**
 * One flow name for the whole builder. The screen travels as `step`, so a
 * funnel is sliced rather than duplicated per screen.
 */
export const PLAN_BUILDER_FLOW = "plan_builder";

/**
 * Hook that provides navigation helpers for plan builder screens.
 * Must be called from within a Stack.Screen (not the parent that renders Stack.Navigator)
 * so that useNavigation() resolves to the nested PlanBuilder navigator.
 */
function usePlanBuilderNav() {
  const navigation =
    useNavigation<NativeStackNavigationProp<PlanBuilderStackParamList>>();
  const t = useTranslations();
  const flow = useFlowAnalytics(PLAN_BUILDER_FLOW);
  const { showToast } = useToast();
  const { state } = usePlanBuilder();
  const { data: patient } = usePatient();
  const updatePatient = useQueuedPatientUpdate();
  const createTestResults = useCreateTestResults();
  const { data: treatmentPlan } = useTreatmentPlan();
  const acceptTreatmentPlan = useAcceptTreatmentPlan();
  const navigationInFlightRef = useRef(false);
  const finalPatientAcceptedRef = useRef(false);
  // Keep a ref to state so the save helper always reads the latest
  const stateRef = useRef(state);
  stateRef.current = state;
  const primaryAddressIdRef = useRef(patient?.addresses?.[0]?.id);
  primaryAddressIdRef.current = patient?.addresses?.[0]?.id;

  /** Persist current builder state to the API */
  const saveProgress = useCallback(async () => {
    await updatePatient.mutateAsync(
      buildPayload(stateRef.current, primaryAddressIdRef.current),
    );
  }, [updatePatient]);

  /** Navigate to the next screen and save progress to the API. */
  const navigateNext = useCallback(
    async (current: PlanBuilderScreen) => {
      if (navigationInFlightRef.current) return;
      navigationInFlightRef.current = true;
      try {
        await saveProgress();
        // Submitted means persisted: an answer that never reached the API is
        // not a step the user completed, and the catch below is the drop-off.
        flow.stepSubmitted(current);
        const next = getNextScreen(stateRef.current, current);
        if (!next) return;
        navigation.navigate(next);
      } catch {
        flow.abandoned("save_failed", { step: current });
        // The queue/gate retains the truthful not-synced state. Do not
        // navigate beyond an answer that lacks durable device ownership, and
        // make the retry path explicit: the same Next action can be used again.
        Alert.alert(
          t("plan_builder.save_failure_title"),
          t("plan_builder.save_failure_detail"),
        );
      } finally {
        navigationInFlightRef.current = false;
      }
    },
    [flow, navigation, saveProgress, t],
  );

  /** Final save on Building mount — persist every accepted plan artifact. */
  const handleSave = useCallback(async () => {
    if (!finalPatientAcceptedRef.current) {
      await saveProgress();
      finalPatientAcceptedRef.current = true;
    }
    const currentState = stateRef.current;

    if (
      shouldAskForAcceptedPlan(currentState) &&
      currentState.acceptedPlanAcknowledged
    ) {
      try {
        await acceptTreatmentPlan.mutateAsync({
          ...currentState.acceptedPlan,
          ...(treatmentPlan ? { expectedVersion: treatmentPlan.version } : {}),
        });
      } catch (error) {
        showToast({
          variant: "error",
          message: t("plan_builder.accepted_plan.save_failed"),
        });
        throw error;
      }
    }

    // Persist self-reported test results as real test result records
    const testPayloads = buildTestResultPayloads(currentState.knownResults);
    if (testPayloads.length > 0) {
      await createTestResults.mutateAsync(testPayloads);
    }
    // Every artifact of the plan is durable at this point, which is what
    // finishing the builder means — the reveal screen after it is a result,
    // not another step to complete.
    flow.completed();
  }, [
    flow,
    saveProgress,
    createTestResults,
    acceptTreatmentPlan,
    treatmentPlan,
    showToast,
    t,
  ]);

  /** Navigate to the reveal screen after animation completes */
  const handleShowReveal = useCallback(() => {
    navigation.navigate("PlanReveal");
  }, [navigation]);

  /** Dismiss the plan builder and return to Home */
  const handleDismiss = useCallback(() => {
    const parent = navigation.getParent();
    if (parent?.canGoBack()) {
      parent.goBack();
    } else {
      navigation.dispatch(CommonActions.navigate({ name: "Home" }));
    }
  }, [navigation]);

  return { navigateNext, handleSave, handleShowReveal, handleDismiss };
}

/** Wrapper components — useNavigation resolves to the nested navigator here */
function GoalRecapWrapper() {
  const { navigateNext } = usePlanBuilderNav();
  return (
    <PlanBuilderScreenLayout>
      <GoalRecapScreen onNext={() => navigateNext("GoalRecap")} />
    </PlanBuilderScreenLayout>
  );
}
function ModalityScreenWrapper() {
  const { navigateNext } = usePlanBuilderNav();
  return (
    <PlanBuilderScreenLayout>
      <ModalityScreen onNext={() => navigateNext("ModalityScreen")} />
    </PlanBuilderScreenLayout>
  );
}
function AcceptedPlanWrapper() {
  const { navigateNext } = usePlanBuilderNav();
  return (
    <PlanBuilderScreenLayout>
      <AcceptedPlanScreen onNext={() => navigateNext("AcceptedPlan")} />
    </PlanBuilderScreenLayout>
  );
}
function PastCyclesWrapper() {
  const { navigateNext } = usePlanBuilderNav();
  return (
    <PlanBuilderScreenLayout>
      <PastCyclesScreen onNext={() => navigateNext("PastCycles")} />
    </PlanBuilderScreenLayout>
  );
}
function PastCycleDetailsWrapper() {
  const { navigateNext } = usePlanBuilderNav();
  return (
    <PlanBuilderScreenLayout>
      <PastCycleDetailsScreen onNext={() => navigateNext("PastCycleDetails")} />
    </PlanBuilderScreenLayout>
  );
}
function ConditionsWrapper() {
  const { navigateNext } = usePlanBuilderNav();
  return (
    <PlanBuilderScreenLayout>
      <ConditionsScreen onNext={() => navigateNext("Conditions")} />
    </PlanBuilderScreenLayout>
  );
}
function EthnicityWrapper() {
  const { navigateNext } = usePlanBuilderNav();
  return (
    <PlanBuilderScreenLayout>
      <EthnicityScreen onNext={() => navigateNext("Ethnicity")} />
    </PlanBuilderScreenLayout>
  );
}
function PartnerStatusWrapper() {
  const { navigateNext } = usePlanBuilderNav();
  return (
    <PlanBuilderScreenLayout>
      <PartnerStatusScreen onNext={() => navigateNext("PartnerStatus")} />
    </PlanBuilderScreenLayout>
  );
}
function ClinicStatusWrapper() {
  const { navigateNext } = usePlanBuilderNav();
  return (
    <PlanBuilderScreenLayout>
      <ClinicStatusScreen onNext={() => navigateNext("ClinicStatus")} />
    </PlanBuilderScreenLayout>
  );
}
function KnownResultsWrapper() {
  const { navigateNext } = usePlanBuilderNav();
  return (
    <PlanBuilderScreenLayout>
      <KnownResultsScreen onNext={() => navigateNext("KnownResults")} />
    </PlanBuilderScreenLayout>
  );
}
function LocationInsuranceWrapper() {
  const { navigateNext } = usePlanBuilderNav();
  return (
    <PlanBuilderScreenLayout>
      <LocationInsuranceScreen
        onNext={() => navigateNext("LocationInsurance")}
      />
    </PlanBuilderScreenLayout>
  );
}
function TimelineWrapper() {
  const { navigateNext } = usePlanBuilderNav();
  return (
    <PlanBuilderScreenLayout>
      <TimelineScreen onNext={() => navigateNext("Timeline")} />
    </PlanBuilderScreenLayout>
  );
}
function FreeTextWrapper() {
  const { navigateNext } = usePlanBuilderNav();
  return (
    <PlanBuilderScreenLayout>
      <FreeTextScreen onNext={() => navigateNext("FreeText")} />
    </PlanBuilderScreenLayout>
  );
}
function BuildingWrapper() {
  const { handleSave, handleShowReveal } = usePlanBuilderNav();
  return (
    <PlanBuilderScreenLayout>
      <BuildingScreen onSave={handleSave} onAnimationDone={handleShowReveal} />
    </PlanBuilderScreenLayout>
  );
}
function PlanRevealWrapper() {
  const { handleDismiss } = usePlanBuilderNav();
  return (
    <PlanBuilderScreenLayout>
      <PlanRevealScreen onDismiss={handleDismiss} />
    </PlanBuilderScreenLayout>
  );
}

function PlanBuilderScreens() {
  const { state } = usePlanBuilder();
  const initialRoute = getFirstIncompleteScreen(state);
  const flow = useFlowAnalytics(PLAN_BUILDER_FLOW);
  // Resuming counts as starting: the builder is entered from Home either way,
  // and the entry screen tells the funnel where the person picked back up.
  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    flow.started({ entry_screen: initialRoute });
  }, [flow, initialRoute]);

  return (
    <Stack.Navigator
      initialRouteName={initialRoute}
      screenListeners={({ route }) => ({
        focus: () => flow.stepViewed(route.name),
      })}
      screenOptions={{
        headerShown: false,
        animation: "slide_from_right",
        gestureEnabled: true,
      }}
    >
      <Stack.Screen name="GoalRecap" component={GoalRecapWrapper} />
      <Stack.Screen name="ModalityScreen" component={ModalityScreenWrapper} />
      <Stack.Screen name="AcceptedPlan" component={AcceptedPlanWrapper} />
      <Stack.Screen name="PastCycles" component={PastCyclesWrapper} />
      <Stack.Screen
        name="PastCycleDetails"
        component={PastCycleDetailsWrapper}
      />
      <Stack.Screen name="Conditions" component={ConditionsWrapper} />
      <Stack.Screen name="Ethnicity" component={EthnicityWrapper} />
      <Stack.Screen name="PartnerStatus" component={PartnerStatusWrapper} />
      <Stack.Screen name="ClinicStatus" component={ClinicStatusWrapper} />
      <Stack.Screen name="KnownResults" component={KnownResultsWrapper} />
      <Stack.Screen
        name="LocationInsurance"
        component={LocationInsuranceWrapper}
      />
      <Stack.Screen name="Timeline" component={TimelineWrapper} />
      <Stack.Screen name="FreeText" component={FreeTextWrapper} />
      <Stack.Screen name="Building" component={BuildingWrapper} />
      <Stack.Screen name="PlanReveal" component={PlanRevealWrapper} />
    </Stack.Navigator>
  );
}

export default function PlanBuilderNavigator() {
  return <PlanBuilderScreens />;
}
